/**
 * TodayV2 — the data side of the new-design Today screen (switch ON only; A24). The page hands it what
 * it already loads; this container adds ONLY what the new screen needs and nothing when the switch is
 * off (it is not mounted then):
 *   - the selection: the NEXT appointment on load (pickNextAppointment), then whatever the clinician
 *     picks. Kept across status changes, so "Mark arrived" never makes the centre jump (Claude Design
 *     verifier finding, round 7).
 *   - the selected patient's GetPatientSummary (the same query and mapping the encounter rail uses);
 *   - "Mark arrived": PUT /api/Appointment/Status with the server's own Arrived status object from the
 *     status list (never an invented body), the encounter status control's path;
 *   - the next free gap from real start/end times.
 */
import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import type { PracticePickerOption } from '@scripsteam/scrips-signal-ds';
import type { ListAppointmentItem } from '@/types/appointment-api';
import { usePatientSummary } from '@/encounter/services/use-patient-summary';
import { patientSummaryToIpsSections } from '@/encounter/lib/to-ips-sections';
import { useStatusList } from '@/services/scheduling/use-status-list';
import { useUpdateAppointmentStatus } from '../services/use-update-appointment-status';
import type { AppointmentBuckets } from '../lib/appointment-buckets';
import { dayTimeline, nextGap, pickNextAppointment } from '../lib/today-overview';
import type { WorklistCardData } from '../lib/to-worklist-card';
import type { BucketKey } from '../todays-appointments-view';
import type { TodayItem } from './today-types';
import { TodayWorklistTemplate } from './TodayWorklistTemplate';
import { practiceNow } from '@/shared/lib/practice-time';

export interface TodayV2Props {
  practices: PracticePickerOption[];
  selectedPracticeId: string;
  onPracticeChange: (id: string) => void;
  search: string;
  onSearch: (v: string) => void;
  activeBucket: BucketKey;
  onBucketChange: (b: BucketKey) => void;
  counts: { upcoming: number; inProgress: number; completed: number };
  buckets: AppointmentBuckets;
  /** Card props for ANY of today's appointments (the page's toWorklistCard, bucket-aware). */
  cardFor: (item: ListAppointmentItem, bucket: BucketKey) => WorklistCardData;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  onOpenVisit: (id: string) => void;
  /** Injectable for tests. */
  now?: () => number;
  /** Hide the worklist's own practice picker (embedded in /dashboard; the top bar's is the only one). */
  hidePracticePicker?: boolean;
}

const patientIdOf = (item: ListAppointmentItem | undefined) => item?.participant?.find((p) => p.patient)?.patient?.id;

export function TodayV2(p: TodayV2Props) {
  const now = p.now ?? (() => practiceNow().getTime());
  const [pickedId, setPickedId] = useState<string>();

  const { items, byId } = useMemo(() => {
    const tagged: { raw: ListAppointmentItem; bucket: BucketKey }[] = [
      ...p.buckets.upcoming.map((raw) => ({ raw, bucket: 'upcoming' as const })),
      ...p.buckets.inProgress.map((raw) => ({ raw, bucket: 'in-progress' as const })),
      ...p.buckets.completed.map((raw) => ({ raw, bucket: 'completed' as const })),
    ];
    const bucketOf = new Map(tagged.map((t) => [t.raw.id, t.bucket]));
    const ordered = dayTimeline(tagged.map((t) => t.raw));
    const list: TodayItem[] = ordered.map((raw) => {
      const bucket = bucketOf.get(raw.id) ?? 'upcoming';
      return {
        id: raw.id,
        card: p.cardFor(raw, bucket),
        bucket,
        start: raw.start,
        end: raw.end,
        statusCode: raw.status?.code ?? '',
      };
    });
    return { items: list, byId: new Map(ordered.map((raw) => [raw.id, raw])) };
    // cardFor is the page's stable mapping of the same data; buckets drive the recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.buckets, p.cardFor]);

  // A one-minute clock for the "next gap" only; the selection never follows it.
  const [nowMs, setNowMs] = useState(() => now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(now()), 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The default is computed only when nothing valid is selected, and then LOCKED IN (Codex P1): a
  // data refresh after its start time must not move the preview to another patient by itself.
  const pickedValid = pickedId != null && byId.has(pickedId);
  const defaultId = useMemo(
    () =>
      pickedValid ? undefined : pickNextAppointment(p.buckets.upcoming, p.buckets.inProgress, now(), p.buckets.completed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [p.buckets, pickedValid],
  );
  useEffect(() => {
    if (!pickedValid && defaultId) setPickedId(defaultId);
  }, [pickedValid, defaultId]);
  const selectedId = pickedValid ? pickedId : defaultId;

  const summary = usePatientSummary(patientIdOf(selectedId ? byId.get(selectedId) : undefined));
  const sections = useMemo(() => (summary.data ? patientSummaryToIpsSections(summary.data) : null), [summary.data]);

  const { data: statusList } = useStatusList();
  const update = useUpdateAppointmentStatus();
  const arrivedStatus = statusList?.find((s) => (s.code ?? '').toLowerCase() === 'arrived');

  const bucketItems = items.filter((it) => it.bucket === p.activeBucket);
  const gap = useMemo(() => nextGap([...byId.values()], nowMs), [byId, nowMs]);

  return (
    <TodayWorklistTemplate
      practices={p.practices}
      hidePracticePicker={p.hidePracticePicker}
      selectedPracticeId={p.selectedPracticeId}
      onPracticeChange={p.onPracticeChange}
      search={p.search}
      onSearch={p.onSearch}
      activeBucket={p.activeBucket}
      onBucketChange={p.onBucketChange}
      counts={p.counts}
      bucketItems={bucketItems}
      dayItems={items}
      loading={p.loading}
      error={p.error}
      onRetry={p.onRetry}
      selectedId={selectedId}
      onSelect={setPickedId}
      sections={sections}
      sectionsLoading={summary.isLoading}
      sectionsError={summary.isError}
      onMarkArrived={(id) => {
        setPickedId(id);
        // The button is disabled without the server's Arrived status object; never invent the body.
        if (!arrivedStatus || update.isPending) return;
        update.mutate({ appointmentId: id, status: arrivedStatus });
      }}
      markingArrived={update.isPending}
      markArrivedUnavailable={!arrivedStatus}
      onOpenVisit={(id) => {
        setPickedId(id);
        p.onOpenVisit(id);
      }}
      gap={gap}
      dateLabel={format(new Date(now()), 'MMMM d')}
    />
  );
}
